import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import produce from 'immer';

import './Reports.scss';
import { Button, ButtonDropdown, ButtonDropdownItem, ErrorBoundary, ErrorMessage, Loader } from 'components';
import { AsyncStatus, StageState } from 'enums';
import { useDisableMainContainerScroll, useLuxonTimezone } from 'hooks';
import { ReportsFilterModal } from './ReportsFilterModal';
import { IField, IReportsFilter, IRow, ISortBy, defaultHiddenColumns, useReportsModel } from './ReportsModel';
import { assertUnreachable, formatDateAndTimeDt, pluralize } from 'services/Utils';
import { IGetAffectedResult, UsersService } from 'services';
import { DragDropContext, Draggable, Droppable, DropResult, ResponderProvided } from 'react-beautiful-dnd';
import { ReportsExportCSVModal } from './ReportsExportCSVModal';
import { ReportsExportColumnModal } from './ReportsExportColumnModal';
import { ReportsFilterType } from './enums/ReportsFilterType';
import { DataSource } from './enums/DataSource';
import { useTranslation } from 'react-i18next';
import { IFilter } from 'interfaces';
import { ReportsTable } from './ReportsTable';
import { ColumnPickerModal } from './ColumnPickerModal';
import { ReportsExportLocationGroupModal } from './ReportsExportLocationGroupModal';

// These are used for the "Stage" and "Filters" selects.
// Some are blank so that they show as selected by default.
const SELECT_VALUE_ALL_STAGES = '';
const SELECT_VALUE_GLOBAL_FILTERS = 'globalFilters';
const SELECT_VALUE_ALL_FILTERS = '';

interface IReportsPageState {
    addSortAscending: boolean,
    editFilter?: IReportsFilter,
    editFilterModalKey: number;
    editFilterModalIsNew?: boolean,
    editFilterModalShow?: boolean,
    exportColumnModalKey: number,
    exportColumnModalShow?: boolean,
    exportLocationGroupModalKey: number,
    exportLocationGroupModalShow?: boolean,
    exportCSVModalKey: number,
    exportCSVModalShow?: boolean,
    columnPickerModalKey: number,
    columnPickerModalShow?: boolean,
}

export function Reports() {
    const rowsChunkSize = 200;

    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const [state, setState] = useState<IReportsPageState>({
        addSortAscending: true,
        editFilterModalKey: 1,
        exportColumnModalKey: 1,
        exportLocationGroupModalKey: 1,
        exportCSVModalKey: 1,
        columnPickerModalKey: 1,
    });
    const {
        addSortAscending,
        editFilter,
        editFilterModalKey,
        editFilterModalIsNew,
        editFilterModalShow,
        exportColumnModalKey,
        exportColumnModalShow,
        exportLocationGroupModalKey,
        exportLocationGroupModalShow,
        exportCSVModalKey,
        exportCSVModalShow,
        columnPickerModalKey,
        columnPickerModalShow,
    } = state;
    const { model } = useReportsModel();
    const { hasTimezone, timezone } = useLuxonTimezone(UsersService.me.timezone);

    const table = model.allTablesById[model.activeTableId];

    // Drag and drop requires a single scroll container, so disable main page scroll.
    const { registerForScrollDisable } = useDisableMainContainerScroll();
    useEffect(() => {
        return registerForScrollDisable('reportsPage');
    }, []);

    // --------------- Loading and URL Stuff ---------------

    function getURL() {
        return '/reports?' + model.getSettingsAsURL();
    }

    // Save our state in the url.
    function saveURL() {
        // NOTE: Our source of truth is our reports model. We don't want to use the URL
        // as the source of truth because we may run into browser character limits,
        // like if a user runs a report that has a filter with 50k locations.
        history.push(getURL());
    }

    // If the user presses the Back/Forward buttons, attempt to reload our state from the url.
    useEffect(() => {
        const unregister = history.listen((location, action) => {
            // Only re-read settings from url if the action is POP, which only happens from the user
            // pressing the Back/Forward button. If we push a router state, the action is PUSH or REPLACE,
            // which we want to ignore.
            if (action === 'POP') {
                model.resetFromURL(location.search, timezone);
                model.load(timezone);
            }
        });
        return unregister;
    }, []);

    // Tell our model to load
    useEffect(() => {
        model.resetFromURL(location.search, timezone);
        // Save our initial params in the URL.
        history.replace(getURL());

        model.load(timezone)
            .catch((err) => {
                console.error('Error loading report: ', err);
            });
    }, []);

    // --------------- Controls Callbacks ---------------

    function onChangeUpdateStageIndex(e) {
        const val = e.target.value;
        let params = {...model.updateParams};

        if (val === SELECT_VALUE_ALL_STAGES) {
            params.isGlobalFilters = false;
            delete params.stageIndex;
            delete params.filterId;
        } else if (val === SELECT_VALUE_GLOBAL_FILTERS) {
            params.isGlobalFilters = true;
            delete params.filterId;
        } else {
            const stageIndex = Number(val);
            const stage = model.outUpdate.rollout?.stages?.[stageIndex];
            if (!stage) {
                console.error(`Invalid stage index, update: `, val, model.outUpdate);
                return;
            }
            params.stageIndex = stageIndex;
            params.isGlobalFilters = false;
            delete params.filterId;
        }

        model.setUpdateParams(params, timezone);
        saveURL();
    }

    function onChangeUpdateFilterId(e) {
        const val = e.target.value;

        model.setUpdateParams({
            ...model.updateParams,
            filterId: val === SELECT_VALUE_ALL_FILTERS ? undefined : Number(val),
        }, timezone);
        saveURL();
    }

    function onChangeIsSnapshot(isSnapshot: boolean) {
        model.setUpdateParams({
            ...model.updateParams,
            isSnapshot: isSnapshot,
        }, timezone)
        saveURL();
    }

    function removeUpdateParams() {
        model.removeUpdateParams(timezone);
        saveURL();
    }

    function onChangeObjectType(e) {
        const tableId = e.target.value;
        model.setActiveTableId(tableId);
        saveURL();
    }

    function onAddFilter() {
        openEditFilterModal(undefined, true);
    }

    function onEditFilter(filterId: string) {
        const filter = model.filters.find(f => f.id === filterId);

        if (filter) {
            openEditFilterModal(filter, false);
        }
    }

    function onSaveFilter(filter: IReportsFilter) {
        const existingIndex = model.filters.findIndex(f => f.id === filter.id);
        
        if (existingIndex != -1) {
            // If we're editing an existing filter, replace it.
            model.filters[existingIndex] = filter;
            model.setFilters(model.filters);
        } else {
            // It's a new filter, concat it.
            model.setFilters([
                ...model.filters,
                filter,
            ]);
        }

        closeEditFilterModal();
        saveURL();
    }

    function onCancelEditFilter() {
        closeEditFilterModal();
    }

    function openEditFilterModal(filter: IReportsFilter, isNewFilter: boolean) {
        setState(produce(draft => {
            draft.editFilter = filter;
            draft.editFilterModalIsNew = isNewFilter;
            // Bump the key so that the modal rerenders
            draft.editFilterModalKey++;
            draft.editFilterModalShow = true;
        }))
    }

    function closeEditFilterModal() {
        setState(produce(draft => {
            draft.editFilterModalShow = false;
        }))
    }

    function onRemoveFilter(filterId: string) {
        const newFilters = model.filters.filter(f => f.id !== filterId);
        model.setFilters(newFilters);
        saveURL();
    }

    function onChangeAddGroup(e) {
        const fieldId = e.target.value;
        model.setGroupBy([
            ...model.groupBy,
            {fieldId: fieldId},
        ]);
        saveURL();
    }

    function onRemoveGroup(groupId: string) {
        const newGroupBy = model.groupBy.filter(g => g.fieldId !== groupId);
        model.setGroupBy(newGroupBy);
        saveURL();
    }

    function onChangeAddSort(e) {
        const fieldId = e.target.value;
        model.setSortBy([
            ...model.sortBy,
            {fieldId: fieldId, ascending: addSortAscending},
        ]);
        saveURL();
    }

    function onClickSort(fieldId: string) {
        const sort = model.sortBy.find(s => s.fieldId === fieldId);
        sort.ascending = !sort.ascending;
        model.setSortBy(model.sortBy);
        saveURL();
    }

    function onRemoveSort(fieldId: string) {
        const newSortBy = model.sortBy.filter(s => s.fieldId !== fieldId);
        model.setSortBy(newSortBy);
        saveURL();
    }

    function toggleAddSortDirection() {
        setState(produce(draft => {
            draft.addSortAscending = !draft.addSortAscending;
        }));
    }

    // If the user clicks a table column header, sort by that field.
    // We don't want to remove all other sorts, it's a nicer experience to just move this field to top priority.
    function onClickColumnHeader(fieldId: string) {
        // See if this field is already the top priority sort field. If it is, toggle the direction.
        if (model.sortBy.length > 0 && model.sortBy[0].fieldId === fieldId) {
            model.sortBy[0].ascending = !model.sortBy[0].ascending;
            model.setSortBy(model.sortBy);
        } else {
            // This field isn't the top priority sort, so we need to put it first. We'll default to ascending.
            const sortByWithoutField = model.sortBy.filter(s => s.fieldId !== fieldId);
            const newSortBy: ISortBy[] = [
                {fieldId: fieldId, ascending: true},
                ...sortByWithoutField,
            ]
            model.setSortBy(newSortBy);
        }
        saveURL();
    }

    function onClickRow(row: IRow, index: number): void {
        // If we're grouping, drill down to the items in the clicked group.
        if (model.groupBy.length > 0) {
            const groupedFieldsById: {[fieldId: string]: IField} = {};
            for (const field of model.outFields) {
                groupedFieldsById[field.id] = field;
            }

            // Get the value of each groupBy field for this row and add them as filters.
            const filtersToAdd = model.getFiltersFromGroupBys(model.groupBy, row);
            model.setFilters(model.filters.concat(filtersToAdd));
            model.setGroupBy([]);
            saveURL();
        }
    }

    const curGroupFieldsByID = {};
    for (const g of model.groupBy) {
        curGroupFieldsByID[g.fieldId] = true;
    }

    const curSortFieldsByID = {};
    for (const s of model.sortBy) {
        curSortFieldsByID[s.fieldId] = true;
    }

    // The available grouping fields are all fields on the table, not just the fields we're showing in the out table.
    const activeTable = model.allTablesById[model.activeTableId];
    const availableSortFields = model.outFields.filter(f => f.isSortable && !curSortFieldsByID[f.id]);
    let availableGroupFields: IField[] = [];
    if (activeTable) {
        availableGroupFields = activeTable.fields.filter(f => f.isGroupable && !curGroupFieldsByID[f.id]);
    }

    function getFieldLabel(fieldId: string): string {
        const f = model.allFieldsById[fieldId];
        if (f) {
            return f.label;
        }

        if (model.loadStatus === AsyncStatus.Loading) {
            return fieldId;
        }

        return `Missing: ${fieldId}`;
    }

    function getFilterDescription(filter: IReportsFilter): string {
        switch (filter.type) {
            case ReportsFilterType.String: {
                switch (filter.comparator) {
                    case 'equal':
                        if (!filter.negateComparison) {
                            return `= ${filter.value}`;
                        }
                        return `!= ${filter.value}`;
                    case 'contains':
                        if (!filter.negateComparison) {
                            return `contains ${filter.value}`;
                        }
                        return `not contains ${filter.value}`;
                    case 'startsWith':
                        if (!filter.negateComparison) {
                            return `starts with ${filter.value}`;
                        }
                        return `not starts with ${filter.value}`;
                    case 'in': {
                        const vals = (filter.value as string[]).join(', ')
                        if (!filter.negateComparison) {
                            return vals;
                        }
                        return `not ${vals}`;
                    }
                    default:
                        return assertUnreachable(filter);
                }
            }
            case ReportsFilterType.Date: {
                const dateTime = formatDateAndTimeDt(filter.value, hasTimezone);
                switch (filter.comparator) {
                    case '<':
                        return `before ${dateTime}`;
                    case '>':
                        return `after ${dateTime}`;
                    default:
                        return assertUnreachable(filter);
                }
            }
            case ReportsFilterType.Number: {
                switch (filter.comparator) {
                    case '<':
                        return `< ${filter.value}`;
                    case '<=':
                        return `<= ${filter.value}`;
                    case '=':
                        return `= ${filter.value}`;
                    case '>':
                        return `> ${filter.value}`;
                    case '>=':
                        return `>= ${filter.value}`;
                    default:
                        return assertUnreachable(filter);
                }
            }
        }
        return assertUnreachable(filter);
    }

    function getFilterTooltip(filter: IReportsFilter): string {
        return `${getFieldLabel(filter.fieldId)} ${getFilterDescription(filter)}`
    }


    const onDragEndFilters = useCallback((result: DropResult, provided: ResponderProvided) => {
        // Make sure drag wasn't canceled
        if (result.reason !== 'DROP') return;
        // Might have dragged to nowhere, in which case destination will be null
        if (!result.destination) return;

        // Remove from source, insert in destination
        const filterId = result.draggableId;
        const index = model.filters.findIndex(f => f.id === filterId);
        if (index === -1) {
            console.error(`Couldn't find filter in array: `, filterId);
            return;
        }
        const [filter] = model.filters.splice(index, 1);
        model.filters.splice(result.destination.index, 0, filter);
        model.setFilters(model.filters);
        saveURL();
    }, []);

    const onDragEndGroupBy = useCallback((result: DropResult, provided: ResponderProvided) => {
        // Make sure drag wasn't canceled
        if (result.reason !== 'DROP') return;
        // Might have dragged to nowhere, in which case destination will be null
        if (!result.destination) return;

        // Remove from source, insert in destination
        const groupByFieldId = result.draggableId;
        const index = model.groupBy.findIndex(g => g.fieldId === groupByFieldId);
        if (index === -1) {
            console.error(`Couldn't find groupBy for fieldId: `, groupByFieldId);
            return;
        }
        const [groupBy] = model.groupBy.splice(index, 1);
        model.groupBy.splice(result.destination.index, 0, groupBy);
        model.setGroupBy(model.groupBy);
        saveURL();
    }, []);

    const onDragEndSortBy = useCallback((result: DropResult, provided: ResponderProvided) => {
        // Make sure drag wasn't canceled
        if (result.reason !== 'DROP') return;
        // Might have dragged to nowhere, in which case destination will be null
        if (!result.destination) return;

        // Remove from source, insert in destination
        const sortByFieldId = result.draggableId;
        const index = model.sortBy.findIndex(s => s.fieldId === sortByFieldId);
        if (index === -1) {
            console.error(`Couldn't find sortBy for fieldId: `, sortByFieldId);
            return;
        }
        const [sortBy] = model.sortBy.splice(index, 1);
        model.sortBy.splice(result.destination.index, 0, sortBy);
        model.setSortBy(model.sortBy);
        saveURL();
    }, []);

    function openExportCSVModal() {
        setState(produce(draft => {
            draft.exportCSVModalShow = true;
            draft.exportCSVModalKey++;
        }))
    }

    function closeExportCSVModal() {
        setState(produce(draft => {
            draft.exportCSVModalShow = false;
        }))
    }

    function openExportColumnModal() {
        setState(produce(draft => {
            draft.exportColumnModalShow = true;
            draft.exportColumnModalKey++;
        }))
    }

    function closeExportColumnModal() {
        setState(produce(draft => {
            draft.exportColumnModalShow = false;
        }))
    }

    function openExportLocationGroupModal() {
        setState(produce(draft => {
            draft.exportLocationGroupModalShow = true;
            draft.exportLocationGroupModalKey++;
        }))
    }

    function closeExportLocationGroupModal() {
        setState(produce(draft => {
            draft.exportLocationGroupModalShow = false;
        }))
    }

    function openColumnPickerModal() {
        setState(produce(draft => {
            draft.columnPickerModalShow = true;
            draft.columnPickerModalKey++;
        }))
    }

    function closeColumnPickerModal() {
        setState(produce(draft => {
            draft.columnPickerModalShow = false;
        }))
    }

    function setVisibleColumns(newVisibleColumns: IField[]) {
        // set all columns hidden
        let newVisible = {...defaultHiddenColumns};
        // show columns from newVisibleColumns
        newVisibleColumns.forEach(f => {
            newVisible[f.id] = true;
        });
        model.setVisibleColumns(newVisible);
        setState(produce(draft => {
            draft.columnPickerModalShow = false;
        }))
    }

    let stageSelectValue = model.updateParams.stageIndex + '';
    if (model.updateParams.isGlobalFilters) {
        stageSelectValue = SELECT_VALUE_GLOBAL_FILTERS;
    }
    
    return (<div className="reportsPage">
        <ErrorBoundary>
            <div className="reports_controlsContainer">
                {model.dataSource === DataSource.Update && model.outUpdate && (<>
                    <Link
                        className="reports_breadcrumb"
                        to={`/updates/${model.outUpdate.id}`}
                        title={model.outUpdate.name}
                    >
                        <FontAwesomeIcon icon="chevron-left" />
                        <span className="reports_breadcrumbLabel">
                            {model.outUpdate.name}
                        </span>
                    </Link>
                    <div className="reports_updateControls">
                        <div className="reports_updateControlsHeader">
                            <button className="reports_removeUpdateFiltersButton" onClick={removeUpdateParams}>
                                <FontAwesomeIcon icon="times" />
                            </button>
                            <FontAwesomeIcon icon="info-circle" />&nbsp;
                            Only showing devices from the update <b>{model.outUpdate.name}</b>.
                        </div>
                        <div className="formItem">
                            <div className="formItem_label">
                                Stage
                            </div>
                            <select
                                className="formItem_select"
                                value={stageSelectValue}
                                onChange={onChangeUpdateStageIndex}
                            >
                                <option value={SELECT_VALUE_ALL_STAGES}>Whole Update</option>
                                <option value={SELECT_VALUE_GLOBAL_FILTERS}>Global Filters</option>
                                {(model.outUpdate.rollout?.stages || []).map((stage, i) => {
                                    let total;
                                    if (stage.state === StageState.Scheduled) {
                                        total = model.outAffected?.rollout?.stages?.[i]?.result?.devicesCount;
                                    } else {
                                        // If the stage has already started, we want to use the db counts.
                                        total = model.outUpdate.counts?.stages?.[i]?.total;
                                    }

                                    return <option value={i} key={i}>
                                        Stage {i + 1} - {t('stageState.' + stage.state)} - {total} devices
                                    </option>
                                })}
                            </select>
                        </div>
                        {(model.updateParams.stageIndex !== undefined || model.updateParams.isGlobalFilters) && (
                            <div className="formItem">
                                <div className="formItem_label">
                                    {model.updateParams.isGlobalFilters && 'Global Filters'}
                                    {!model.updateParams.isGlobalFilters && 'Stage Filters'}
                                </div>
                                <select
                                    className="formItem_select"
                                    value={model.updateParams.filterId + ''}
                                    onChange={onChangeUpdateFilterId}
                                >
                                    <option value={SELECT_VALUE_ALL_FILTERS}>
                                        {model.updateParams.isGlobalFilters && 'Combined Filters'}
                                        {!model.updateParams.isGlobalFilters && 'Whole Stage'}
                                    </option>
                                    {(() => {
                                        const stageIndex = model.updateParams.stageIndex;
                                        let filters: IFilter[];

                                        if (model.updateParams.isGlobalFilters) {
                                            filters = model.outUpdate.rollout?.global?.filters || [];
                                        } else {
                                            // Iterate the stage's filters so we can get their id's.
                                            const stage = model.outUpdate.rollout?.stages?.[stageIndex];
                                            if (!stage) {
                                                console.error(`Couldn't find stage index: ${stageIndex}`);
                                                return null;
                                            }
                                            filters = stage.filters;
                                        }

                                        return filters.map((f) => {
                                            // Try and lookup the devices count.
                                            let result: IGetAffectedResult;
                                            if (model.updateParams.isGlobalFilters) {
                                                result = model.outAffected?.rollout?.global?.filters?.[f.id];
                                            } else {
                                                result = model.outAffected?.rollout?.stages?.[stageIndex]?.filters?.[f.id];
                                            }
                                            let devicesCount = result?.devicesCount || 0;

                                            return <option key={f.id} value={f.id}>
                                                {t(`filterType.${f.type}`)} - {devicesCount} devices
                                            </option>
                                        });
                                    })()}
                                </select>
                            </div>
                        )}
                        <div className="formItem">
                            <div className="formItem_label">
                                View
                            </div>
                            <div>
                                <div className="btnGroup">
                                    <Button
                                        size="small" 
                                        theme="blueOutline" 
                                        btnGroupSelected={!model.updateParams.isSnapshot}
                                        onClick={() => onChangeIsSnapshot(false)}
                                    >
                                        Targets
                                    </Button>
                                    <Button
                                        size="small"
                                        theme="blueOutline"
                                        btnGroupSelected={model.updateParams.isSnapshot}
                                        onClick={() => onChangeIsSnapshot(true)}
                                    >
                                        Results
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>)}
                <div className="formItem">
                    <div className="formItem_label">
                        Table
                    </div>
                    <select
                        className="formItem_select"
                        value={model.activeTableId}
                        onChange={onChangeObjectType}
                    >
                        {model.allTables.map((table) => (
                            <option value={table.id} key={table.id}>{table.label}</option>
                        ))}
                    </select>
                </div>
                <div className="formItem">
                    <div className="formItem_label">
                        Filters
                    </div>
                    <DragDropContext onDragEnd={onDragEndFilters} >
                        <Droppable
                            droppableId="filters"
                            direction="vertical"
                        >
                            {(dropProvided, dropSnapshot) => (
                                <div
                                    className="reports_controlsList"
                                    ref={dropProvided.innerRef}
                                    {...dropProvided.droppableProps}
                                >
                                    {model.filters.map((filter, i) => (       
                                        <Draggable key={filter.id} draggableId={filter.id} index={i}>
                                            {(dragProvided, dragSnapshot) => (                             
                                                <div
                                                    className="reports_controlsListItem clickable"
                                                    ref={dragProvided.innerRef}
                                                    {...dragProvided.draggableProps}
                                                    {...dragProvided.dragHandleProps}
                                                >
                                                    <div
                                                        className="reports_controlsListItem_label"
                                                        onClick={() => onEditFilter(filter.id)}
                                                        title={getFilterTooltip(filter)}
                                                    >
                                                        <div className="ellipsis">
                                                            {getFieldLabel(filter.fieldId)}
                                                        </div>
                                                        <div className="ellipsis">
                                                            {getFilterDescription(filter)}
                                                        </div>
                                                    </div>
                                                    <button
                                                        className="reports_controlsListItem_closeButton"
                                                        onClick={() => onRemoveFilter(filter.id)}
                                                    >
                                                        <FontAwesomeIcon icon="times" />
                                                    </button>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {dropProvided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                    {table && (
                        <Button theme="blueOutline" size="small" onClick={onAddFilter}>
                            Add Filter
                        </Button>
                    )}
                </div>
                <div className="formItem">
                    <div className="formItem_label">
                        Group By
                    </div>
                    <DragDropContext onDragEnd={onDragEndGroupBy}>
                        <Droppable
                            droppableId="groupBy"
                            direction="vertical"
                        >
                            {(dropProvided, dropSnapshot) => (
                                <div 
                                    className="reports_controlsList"
                                    ref={dropProvided.innerRef}
                                    {...dropProvided.droppableProps}
                                >
                                    {model.groupBy.map((group, i) => {
                                        return (
                                            <Draggable key={group.fieldId} draggableId={group.fieldId} index={i}>
                                                {(dragProvided, dragSnapshot) => ( 
                                                    <div
                                                        className="reports_controlsListItem"
                                                        ref={dragProvided.innerRef}
                                                        {...dragProvided.draggableProps}
                                                        {...dragProvided.dragHandleProps}
                                                    >
                                                        <div className="reports_controlsListItem_label">
                                                            {getFieldLabel(group.fieldId)}
                                                        </div>
                                                        <button
                                                            className="reports_controlsListItem_closeButton"
                                                            onClick={() => onRemoveGroup(group.fieldId)}
                                                        >
                                                            <FontAwesomeIcon icon="times" />
                                                        </button>
                                                    </div>
                                                )}
                                            </Draggable>
                                        );
                                    })}
                                    {dropProvided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                    {availableGroupFields.length > 0 && (
                        <select
                            className="formItem_select"
                            value=""
                            onChange={onChangeAddGroup}
                        >
                            <option value="">Add Grouping</option>
                            {availableGroupFields.map((field) => (
                                <option value={field.id} key={field.id}>
                                    {field.label}
                                </option>
                            ))}
                        </select>
                    )}
                </div>
                <div className="formItem">
                    <div className="formItem_label">
                        Sort By
                    </div>
                    <DragDropContext onDragEnd={onDragEndSortBy}>
                        <Droppable
                            droppableId="sortBy"
                            direction="vertical"
                        >
                            {(dropProvided, dropSnapshot) => (
                                <div
                                    className="reports_controlsList"
                                    ref={dropProvided.innerRef}
                                    {...dropProvided.droppableProps}
                                >
                                    {model.sortBy.map((sort, i) => {
                                        return (
                                            <Draggable key={sort.fieldId} draggableId={sort.fieldId} index={i}>
                                                {(dragProvided, dragSnapshot) => ( 
                                                    <div
                                                        className="reports_controlsListItem clickable"
                                                        ref={dragProvided.innerRef}
                                                        {...dragProvided.draggableProps}
                                                        {...dragProvided.dragHandleProps}
                                                    >
                                                        <div 
                                                            className="reports_controlsListItem_label"
                                                            onClick={() => onClickSort(sort.fieldId)}
                                                        >
                                                            {getFieldLabel(sort.fieldId)}
                                                            {sort.ascending && <FontAwesomeIcon icon="long-arrow-alt-down" className="reports_controlsListItem_sortIcon" />}
                                                            {!sort.ascending && <FontAwesomeIcon icon="long-arrow-alt-up" className="reports_controlsListItem_sortIcon" />}
                                                        </div>
                                                        <button
                                                            className="reports_controlsListItem_closeButton"
                                                            onClick={() => onRemoveSort(sort.fieldId)}
                                                        >
                                                            <FontAwesomeIcon icon="times" />
                                                        </button>
                                                    </div>
                                                )}
                                            </Draggable>
                                        );
                                    })}
                                    {dropProvided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                    {availableSortFields.length > 0 && (
                        <div className="reports_sortControlsWrap">
                            <select
                                className="formItem_select reports_addSortSelect"
                                value=""
                                onChange={onChangeAddSort}
                            >
                                <option value="">Add Sort Field</option>
                                {availableSortFields.map((field) => (
                                    <option value={field.id} key={field.id}>
                                        {field.label}
                                    </option>
                                ))}
                            </select>
                            <button
                                className="reports_addSortDirectionButton"
                                onClick={toggleAddSortDirection}
                                type="button"
                            >
                                {addSortAscending && <FontAwesomeIcon icon="long-arrow-alt-down" />}
                                {!addSortAscending && <FontAwesomeIcon icon="long-arrow-alt-up" />}
                            </button>
                        </div>
                    )}
                </div>
            </div>
            <div className="reports_resultsContainer">
                <ErrorBoundary>
                    {model.loadStatus === AsyncStatus.Loading && (
                        <Loader />
                    )}
                    {model.loadStatus === AsyncStatus.Error && (
                        <ErrorMessage message={model.loadError} />
                    )}
                    {model.loadStatus === AsyncStatus.Success && (<>
                        <div className="reports_tableControls">
                            <div className="reports_resultsCount">
                                {model.outRows.length} {pluralize(model.outRows.length, 'item', 'items')}
                            </div>
                            <div className="reports_lastModified">
                                {model.dataModifiedAtDt?.isValid && ('Data refreshed at: ' + model.dataModifiedAtDt.toLocaleString({
                                    month: 'short',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    second: '2-digit',
                                }))}
                            </div>
                            <div className="reports_exportButtonWrap">
                                <ButtonDropdown
                                    buttonProps={{
                                        theme: "blueOutline",
                                        size: "tiny",
                                    }}
                                    buttonContent={(<>
                                        Export <FontAwesomeIcon icon="chevron-down" />
                                    </>)}
                                >
                                    <ButtonDropdownItem onClick={openExportCSVModal}>
                                        Export to CSV
                                    </ButtonDropdownItem>
                                    <ButtonDropdownItem onClick={openExportColumnModal}>
                                        Export Column
                                    </ButtonDropdownItem>
                                    <ButtonDropdownItem onClick={openExportLocationGroupModal}>
                                        Export Location Group
                                    </ButtonDropdownItem>
                                </ButtonDropdown>
                                <Button theme="blueOutline" size="tiny" onClick={openColumnPickerModal}>
                                    <FontAwesomeIcon icon="cog" />
                                </Button>
                            </div>
                        </div>
                        <div className="reports_tableContainer">
                            <ReportsTable
                                model={model} 
                                onClickColumnHeader={onClickColumnHeader}
                                onClickRow={onClickRow}
                            />
                        </div>
                    </>)}
                </ErrorBoundary>
            </div>
            {model.loadStatus === AsyncStatus.Success && table && (<>
                <ReportsFilterModal
                    key={'editFilterModal_' + editFilterModalKey} 
                    defaultFilter={editFilter}
                    isNewFilter={editFilterModalIsNew}
                    model={model}
                    show={editFilterModalShow}
                    onCancel={onCancelEditFilter}
                    onSave={onSaveFilter}
                />
                <ReportsExportColumnModal
                    key={'exportColumnModal_' + exportColumnModalKey}
                    model={model}
                    onHide={closeExportColumnModal}
                    show={exportColumnModalShow}
                />
                <ReportsExportLocationGroupModal
                    key={'exportLocationGroupModal_' + exportLocationGroupModalKey}
                    model={model}
                    onHide={closeExportLocationGroupModal}
                    show={exportLocationGroupModalShow}
                />
                <ReportsExportCSVModal
                    key={'exportCSVModal_' + exportCSVModalKey}
                    model={model}
                    onHide={closeExportCSVModal}
                    show={exportCSVModalShow}
                />
                <ColumnPickerModal
                    key={'columnPickerModal_' + columnPickerModalKey}
                    visibleColumns={model.outFields}
                    setVisibleColumns={setVisibleColumns}
                    onHide={closeColumnPickerModal}
                    show={columnPickerModalShow}
                />
            </>)}
        </ErrorBoundary>
    </div>);
}